/**
 * RecommendationsPanel — grid of AI-generated recommendation cards.
 *
 * Loading state: 3 skeleton cards.
 * Loaded state: cards with priority badge, area, action, impact, governance reference.
 */

import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { AIRecommendation } from '@/types/rca';

// ============================================================
// Helpers
// ============================================================

interface PriorityStyle {
  badge: string;
  border: string;
}

function priorityStyle(priority: AIRecommendation['priority']): PriorityStyle {
  switch (priority) {
    case 'High':
      return {
        badge: 'bg-risk-high/10 text-risk-high border-risk-high/30',
        border: 'border-l-risk-high',
      };
    case 'Medium':
      return {
        badge: 'bg-risk-medium/10 text-risk-medium border-risk-medium/30',
        border: 'border-l-risk-medium',
      };
    case 'Low':
      return {
        badge: 'bg-risk-low/10 text-risk-low border-risk-low/30',
        border: 'border-l-risk-low',
      };
  }
}

// ============================================================
// Types
// ============================================================

interface RecommendationsPanelProps {
  recommendations: AIRecommendation[];
  isLoading: boolean;
}

// ============================================================
// Sub-components
// ============================================================

function SkeletonCard() {
  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-3 w-3/4" />
    </div>
  );
}

function RecommendationCard({ rec }: { rec: AIRecommendation }) {
  const styles = priorityStyle(rec.priority);

  return (
    <div
      className={cn(
        'relative rounded-lg border bg-card p-5 space-y-3',
        'border-l-4 transition-all duration-200 hover:shadow-medium hover:-translate-y-0.5',
        styles.border,
        'border-border',
      )}
    >
      {/* Priority badge */}
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {rec.area}
        </span>
        <Badge
          variant="outline"
          className={cn('text-xs font-semibold shrink-0', styles.badge)}
        >
          {rec.priority} Priority
        </Badge>
      </div>

      {/* Action */}
      <p className="text-sm font-medium text-foreground leading-snug">{rec.action}</p>

      {/* Expected Impact */}
      {rec.impact && (
        <div className="space-y-0.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Expected Impact
          </p>
          <p className="text-sm text-muted-foreground leading-snug">{rec.impact}</p>
        </div>
      )}

      {/* Governance reference */}
      {rec.governance_reference && (
        <p className="text-xs text-muted-foreground/70 italic border-t border-border/60 pt-2 mt-2">
          {rec.governance_reference}
        </p>
      )}
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export function RecommendationsPanel({ recommendations, isLoading }: RecommendationsPanelProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">AI Recommendations</h3>
        <div className="grid gap-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">AI Recommendations</h3>
        <div className="rounded-lg border border-border bg-muted/30 px-6 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            No recommendations available. Complete the assessment to generate AI recommendations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-foreground">
        AI Recommendations
        <span className="ml-2 text-xs text-muted-foreground font-normal">
          ({recommendations.length})
        </span>
      </h3>
      <div className="grid gap-4">
        {recommendations.map((rec, idx) => (
          <RecommendationCard key={`${rec.area}-${idx}`} rec={rec} />
        ))}
      </div>
    </div>
  );
}
